<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { apiUrlRoot } from "@/vars";
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";

dayjs.extend(relativeTime);
const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["refreshed"]);
if (!props.embedded) useBreadcrumbs().setBreadcrumb("Competitors");

const route = useRoute();
const router = useRouter();
const competitors = ref([]);
const loading = ref(false);
const saving = ref(false);
const refreshing = ref(new Set());
const errors = ref({});
const editing = ref(false);
const openReportId = ref(Number(route.query.competitor ?? 0) || null);
const monitorFeedback = ref({});

const emptyForm = () => ({
  id: null,
  name: "",
  company_number: "",
  website_urls: "",
  notes: "",
  active: true,
  monitoring_interval_hours: 24,
});
const form = ref(emptyForm());
const activeCompetitors = computed(() =>
  competitors.value.filter((entry) => entry.active),
);

const setReportQuery = (competitorId = null) => {
  const query = { ...route.query };
  if (props.embedded) query.category = "competitor";
  if (competitorId) query.competitor = competitorId;
  else delete query.competitor;
  router.replace({ query });
};

const load = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get("/api/manager/competitors", {
      suppressErrorAlert: true,
    });
    competitors.value = response.data ?? [];
    const requestedId = Number(route.query.competitor ?? 0);
    openReportId.value = competitors.value.some(
      (entry) => entry.id === requestedId,
    )
      ? requestedId
      : null;
  } finally {
    loading.value = false;
  }
};

const startCreate = () => {
  form.value = emptyForm();
  errors.value = {};
  editing.value = true;
};

const edit = (competitor) => {
  form.value = {
    id: competitor.id,
    name: competitor.name,
    company_number: competitor.company?.company_number ?? "",
    website_urls: competitor.websites.map((website) => website.url).join("\n"),
    notes: competitor.notes ?? "",
    active: competitor.active,
    monitoring_interval_hours:
      competitor.websites[0]?.monitoring_interval_hours ?? 24,
  };
  errors.value = {};
  editing.value = true;
};

const closeEditor = () => {
  form.value = emptyForm();
  errors.value = {};
  editing.value = false;
};

const toggleReport = (competitor) => {
  const nextId = openReportId.value === competitor.id ? null : competitor.id;
  openReportId.value = nextId;
  setReportQuery(nextId);
};

const isReportOpen = (id) => openReportId.value === id;

const payload = () => ({
  name: form.value.name,
  company_number: form.value.company_number || null,
  website_urls: form.value.website_urls
    .split(/\r?\n/)
    .map((value) => value.trim())
    .filter(Boolean),
  notes: form.value.notes || null,
  active: form.value.active,
  monitoring_interval_hours: Number(form.value.monitoring_interval_hours || 24),
});

const save = async () => {
  saving.value = true;
  errors.value = {};
  try {
    if (form.value.id) {
      await apiClient.put(
        `/api/manager/competitors/${form.value.id}`,
        payload(),
        { suppressErrorAlert: true },
      );
    } else {
      await apiClient.post("/api/manager/competitors", payload(), {
        suppressErrorAlert: true,
      });
    }
    closeEditor();
    await load();
    emit("refreshed");
  } catch (error) {
    errors.value = error?.response?.data?.errors ?? {
      general: [
        error?.response?.data?.message ?? "Unable to save this competitor.",
      ],
    };
  } finally {
    saving.value = false;
  }
};

const remove = async (competitor) => {
  if (
    !window.confirm(
      `Stop monitoring ${competitor.name}? Existing feed history will be retained.`,
    )
  )
    return;
  await apiClient.delete(`/api/manager/competitors/${competitor.id}`);
  if (form.value.id === competitor.id) closeEditor();
  await load();
};

const monitorNow = async (competitor) => {
  refreshing.value = new Set([...refreshing.value, competitor.id]);
  monitorFeedback.value = { ...monitorFeedback.value, [competitor.id]: null };
  try {
    const response = await apiClient.post(
      `/api/manager/competitors/${competitor.id}/monitor`,
      {},
      { suppressErrorAlert: true },
    );
    const updated = response.data?.competitor;
    if (updated) {
      competitors.value = competitors.value.map((entry) =>
        entry.id === updated.id ? updated : entry,
      );
    }
    monitorFeedback.value = {
      ...monitorFeedback.value,
      [competitor.id]: response.data?.message ?? "Competitor report refreshed.",
    };
    openReportId.value = competitor.id;
    setReportQuery(competitor.id);
    emit("refreshed");
  } catch (error) {
    monitorFeedback.value = {
      ...monitorFeedback.value,
      [competitor.id]:
        error?.response?.data?.errors?.monitor?.[0] ??
        error?.response?.data?.message ??
        "The competitor report could not be refreshed.",
    };
  } finally {
    const next = new Set(refreshing.value);
    next.delete(competitor.id);
    refreshing.value = next;
  }
};

const isRefreshing = (id) => refreshing.value.has(id);
const formatRelative = (date) =>
  date ? dayjs(date).fromNow() : "Never checked";
const mediaUrl = (value) => {
  if (!value) return null;
  try {
    return new URL(value, apiUrlRoot).toString();
  } catch {
    return value;
  }
};
const reportParagraphs = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return [];

  const explicitParagraphs = text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  return explicitParagraphs.flatMap((paragraph) => {
    if (paragraph.length <= 360) return [paragraph];

    const sentences = paragraph
      .match(/[^.!?]+(?:[.!?]+(?=\s|$)|$)/g)
      ?.map((sentence) => sentence.trim())
      .filter(Boolean) ?? [paragraph];
    const chunks = [];
    let chunk = "";
    sentences.forEach((sentence) => {
      if (chunk && `${chunk} ${sentence}`.length > 360) {
        chunks.push(chunk);
        chunk = sentence;
      } else {
        chunk = [chunk, sentence].filter(Boolean).join(" ");
      }
    });
    if (chunk) chunks.push(chunk);

    return chunks.length > 1
      ? chunks
      : (paragraph.match(/.{1,360}(?:\s|$)/g) ?? [paragraph]);
  });
};
const fieldError = (field) => errors.value?.[field]?.[0] ?? null;
const websiteError = computed(() => {
  const entry = Object.entries(errors.value ?? {}).find(([field]) =>
    field.startsWith("website_urls."),
  );
  return entry?.[1]?.[0] ?? null;
});
const riskClasses = (band) =>
  ({
    critical:
      "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-200",
    high: "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-200",
    moderate:
      "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200",
    low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200",
  })[band] ??
  "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200";

watch(
  () => route.query.competitor,
  (value) => {
    const requestedId = Number(value ?? 0);
    openReportId.value = requestedId || null;
  },
);

onMounted(load);
</script>

<template>
  <div :class="props.embedded ? 'space-y-3' : 'space-y-5 pb-12'">
    <div
      v-if="!props.embedded"
      class="flex flex-wrap items-end justify-between gap-4"
    >
      <div>
        <p
          class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
        >
          Management overview
        </p>
        <h1 class="mt-1 text-2xl font-semibold text-slate-950 dark:text-white">
          Competitors
        </h1>
        <p class="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-300">
          Monitor public websites and Companies House health. Material changes
          appear automatically in the management News Feed.
        </p>
      </div>
      <div>
        <button
          type="button"
          class="btn accent inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap px-3"
          @click="startCreate"
        >
          <PlusIcon class="mr-1.5 h-4 w-4" /> Add competitor
        </button>
      </div>
    </div>

    <div
      v-else
      class="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-5"
    >
      <div>
        <h2 class="font-semibold text-slate-900 dark:text-slate-100">
          Competitor monitoring
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Add websites and Companies House records. Logos are collected once
          from each competitor’s primary website.
        </p>
      </div>
      <button
        type="button"
        class="btn accent inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap px-3"
        @click="startCreate"
      >
        <PlusIcon class="mr-1.5 h-4 w-4" /> Add competitor
      </button>
    </div>

    <div
      class="grid items-start gap-6"
      :class="editing ? 'xl:grid-cols-[minmax(0,1fr)_26rem]' : ''"
    >
      <section>
        <div v-if="loading" class="space-y-3">
          <div
            v-for="index in 4"
            :key="index"
            class="h-64 animate-pulse rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
          />
        </div>

        <div v-else-if="activeCompetitors.length" class="space-y-3">
          <article
            v-for="competitor in activeCompetitors"
            :key="competitor.id"
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex min-w-0 items-start gap-3">
                <div
                  class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300"
                >
                  <img
                    v-if="competitor.logo_url"
                    :src="mediaUrl(competitor.logo_url)"
                    :alt="`${competitor.name} logo`"
                    class="h-full w-full rounded-full object-contain p-1.5"
                    loading="lazy"
                    referrerpolicy="no-referrer"
                    @error="competitor.logo_url = null"
                  />
                  <BuildingOffice2Icon v-else class="h-6 w-6" />
                </div>
                <div class="min-w-0">
                  <h2
                    class="truncate text-lg font-semibold text-slate-950 dark:text-white"
                  >
                    {{ competitor.name }}
                  </h2>
                  <p v-if="competitor.company" class="text-xs text-slate-400">
                    {{ competitor.company.company_name }} ·
                    {{ competitor.company.company_number }}
                  </p>
                  <p v-else class="text-xs text-slate-400">
                    No Companies House company linked
                  </p>
                </div>
              </div>
              <span
                v-if="competitor.company?.risk_band"
                class="rounded-full px-2.5 py-1 text-xs font-semibold capitalize"
                :class="riskClasses(competitor.company.risk_band)"
              >
                {{ competitor.company.risk_band }} risk
              </span>
            </div>

            <div
              v-if="competitor.company"
              class="mt-4 rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-900/40"
            >
              <div class="flex items-center justify-between gap-2">
                <span class="font-medium text-slate-700 dark:text-slate-200"
                  >Companies House</span
                >
                <span class="capitalize text-slate-500">{{
                  competitor.company.company_status || "Unknown"
                }}</span>
              </div>
              <p
                v-if="competitor.company.risk_summary"
                class="mt-2 line-clamp-3 text-xs leading-5 text-slate-500 dark:text-slate-300"
              >
                {{ competitor.company.risk_summary }}
              </p>
              <p
                v-if="competitor.company.last_error"
                class="mt-2 text-xs text-rose-600"
              >
                {{ competitor.company.last_error }}
              </p>
            </div>

            <div class="mt-4">
              <p
                class="text-xs font-bold uppercase tracking-wide text-slate-400"
              >
                Websites
              </p>
              <ul v-if="competitor.websites.length" class="mt-2 space-y-2">
                <li
                  v-for="website in competitor.websites"
                  :key="website.id"
                  class="rounded-lg border border-slate-100 px-3 py-2 dark:border-slate-700"
                >
                  <div
                    class="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    <GlobeAltIcon class="h-4 w-4 shrink-0 text-cyan-500" />
                    <span class="truncate" :title="website.url">{{
                      website.label || website.url
                    }}</span>
                  </div>
                  <div
                    class="mt-1 flex items-center gap-1.5 text-xs"
                    :class="
                      website.last_error ? 'text-rose-600' : 'text-slate-400'
                    "
                  >
                    <ExclamationTriangleIcon
                      v-if="website.last_error"
                      class="h-3.5 w-3.5"
                    />
                    <CheckCircleIcon
                      v-else-if="website.last_successful_at"
                      class="h-3.5 w-3.5 text-emerald-500"
                    />
                    <span>{{
                      website.last_error ||
                      formatRelative(website.last_successful_at)
                    }}</span>
                  </div>
                  <p
                    v-if="website.latest_snapshot"
                    class="mt-2 truncate text-xs text-slate-500 dark:text-slate-300"
                  >
                    Latest report:
                    {{
                      website.latest_snapshot.page_title ||
                      website.latest_snapshot.page_description ||
                      "Website captured"
                    }}
                  </p>
                </li>
              </ul>
              <p v-else class="mt-2 text-sm text-slate-400">
                No websites configured.
              </p>
            </div>

            <p
              v-if="competitor.notes"
              class="mt-4 line-clamp-2 text-sm text-slate-500 dark:text-slate-300"
            >
              {{ competitor.notes }}
            </p>

            <section
              v-if="isReportOpen(competitor.id)"
              class="mt-5 overflow-hidden rounded-xl border border-cyan-100 bg-cyan-50/40 dark:border-cyan-900/60 dark:bg-slate-900/50"
            >
              <div
                class="flex flex-wrap items-start justify-between gap-3 border-b border-cyan-100 px-4 py-3 dark:border-slate-700"
              >
                <div>
                  <div class="flex items-center gap-2">
                    <DocumentMagnifyingGlassIcon
                      class="h-5 w-5 text-cyan-600 dark:text-cyan-300"
                    />
                    <h3
                      class="text-lg font-semibold text-slate-900 dark:text-slate-100"
                    >
                      Latest competitor report
                    </h3>
                  </div>
                  <p
                    class="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-300"
                  >
                    Information captured and stored by Neptune. Images below are
                    served locally.
                  </p>
                </div>
                <span
                  v-if="competitor.logo_checked_at && !competitor.logo_url"
                  class="rounded-full bg-white px-2.5 py-1 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  No site logo found
                </span>
              </div>

              <div class="space-y-4 p-4">
                <div
                  v-if="competitor.company"
                  class="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800 sm:grid-cols-[10rem_1fr]"
                >
                  <div>
                    <p
                      class="text-sm font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-300"
                    >
                      Company health
                    </p>
                    <p
                      class="mt-1 font-semibold capitalize text-slate-800 dark:text-slate-100"
                    >
                      {{ competitor.company.risk_band || "Not assessed" }}
                    </p>
                    <p class="text-sm text-slate-500 dark:text-slate-300">
                      {{
                        competitor.company.company_status || "Unknown status"
                      }}
                    </p>
                  </div>
                  <p
                    class="text-base leading-7 text-slate-600 dark:text-slate-200"
                  >
                    {{
                      competitor.company.risk_summary ||
                      "Companies House monitoring is active. A health summary will appear when the assessment completes."
                    }}
                  </p>
                </div>

                <article
                  v-for="website in competitor.websites"
                  :key="`report-${website.id}`"
                  class="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                >
                  <div
                    class="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
                  >
                    <div class="min-w-0">
                      <p
                        class="truncate text-base font-semibold text-slate-800 dark:text-slate-100"
                      >
                        {{ website.label || website.url }}
                      </p>
                      <p
                        class="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-300"
                      >
                        <template v-if="website.latest_snapshot">
                          Captured
                          {{
                            formatRelative(website.latest_snapshot.observed_at)
                          }}
                          · HTTP
                          {{ website.latest_snapshot.http_status || "—" }} ·
                          {{ website.snapshot_count }}
                          {{
                            website.snapshot_count === 1 ? "report" : "reports"
                          }}
                        </template>
                        <template v-else>
                          Waiting for the first website report
                        </template>
                      </p>
                    </div>
                    <span
                      v-if="website.last_error"
                      class="rounded-full bg-rose-50 px-2.5 py-1 text-sm font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-200"
                    >
                      Check failed
                    </span>
                    <span
                      v-else-if="website.latest_snapshot"
                      class="rounded-full bg-emerald-50 px-2.5 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                    >
                      Report ready
                    </span>
                  </div>

                  <div
                    v-if="website.latest_snapshot"
                    class="border-t border-slate-100 dark:border-slate-700"
                  >
                    <img
                      v-if="website.latest_snapshot.image_url"
                      :src="mediaUrl(website.latest_snapshot.image_url)"
                      :alt="
                        website.latest_snapshot.page_title || competitor.name
                      "
                      class="max-h-80 w-full object-cover"
                      loading="lazy"
                      referrerpolicy="no-referrer"
                      @error="website.latest_snapshot.image_url = null"
                    />
                    <div class="space-y-2 p-4">
                      <h4
                        v-if="website.latest_snapshot.page_title"
                        class="font-semibold text-slate-800 dark:text-slate-100"
                      >
                        {{ website.latest_snapshot.page_title }}
                      </h4>
                      <p
                        v-if="website.latest_snapshot.page_description"
                        class="text-base leading-7 text-slate-600 dark:text-slate-200"
                      >
                        {{ website.latest_snapshot.page_description }}
                      </p>
                      <div
                        v-if="website.latest_snapshot.content_excerpt"
                        class="mt-4 border-t border-slate-100 pt-4 dark:border-slate-700"
                      >
                        <p
                          class="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300"
                        >
                          Report summary
                        </p>
                        <div class="space-y-3">
                          <p
                            v-for="(paragraph, index) in reportParagraphs(
                              website.latest_snapshot.content_excerpt,
                            )"
                            :key="`${website.id}-paragraph-${index}`"
                            class="text-base leading-7 text-slate-700 dark:text-slate-100"
                          >
                            {{ paragraph }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p
                    v-else
                    class="border-t border-slate-100 px-4 py-5 text-base leading-7 text-slate-500 dark:border-slate-700 dark:text-slate-300"
                  >
                    Use “Check now” to create the first report immediately.
                  </p>
                  <p
                    v-if="website.last_error"
                    class="border-t border-rose-100 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-200"
                  >
                    {{ website.last_error }}
                  </p>
                </article>

                <p
                  v-if="!competitor.websites.length"
                  class="rounded-lg bg-white px-4 py-5 text-base leading-7 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                >
                  Add at least one website to generate a website report.
                </p>
              </div>
            </section>

            <p
              v-if="monitorFeedback[competitor.id]"
              class="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-600 dark:bg-slate-900/50 dark:text-slate-200"
            >
              {{ monitorFeedback[competitor.id] }}
            </p>

            <footer
              class="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 dark:border-slate-700"
            >
              <div class="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  class="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 dark:text-cyan-300 dark:hover:bg-cyan-950/30"
                  @click="toggleReport(competitor)"
                >
                  <DocumentMagnifyingGlassIcon class="mr-1.5 h-4 w-4" />
                  {{
                    isReportOpen(competitor.id) ? "Hide report" : "View report"
                  }}
                  <ChevronUpIcon
                    v-if="isReportOpen(competitor.id)"
                    class="ml-1 h-3.5 w-3.5"
                  />
                  <ChevronDownIcon v-else class="ml-1 h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  class="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700"
                  :disabled="isRefreshing(competitor.id)"
                  @click="monitorNow(competitor)"
                >
                  <ArrowPathIcon
                    class="mr-1 inline h-4 w-4"
                    :class="isRefreshing(competitor.id) ? 'animate-spin' : ''"
                  />
                  {{ isRefreshing(competitor.id) ? "Checking…" : "Check now" }}
                </button>
              </div>
              <div class="flex gap-1">
                <button
                  type="button"
                  class="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
                  title="Edit competitor"
                  @click="edit(competitor)"
                >
                  <PencilSquareIcon class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  class="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30"
                  title="Remove competitor"
                  @click="remove(competitor)"
                >
                  <TrashIcon class="h-4 w-4" />
                </button>
              </div>
            </footer>
          </article>
        </div>

        <div
          v-else-if="!loading"
          class="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-600 dark:bg-slate-800"
        >
          <BuildingOffice2Icon class="mx-auto h-12 w-12 text-slate-300" />
          <h2 class="mt-3 text-lg font-semibold text-slate-800 dark:text-white">
            No competitors monitored yet
          </h2>
          <p class="mt-1 text-sm text-slate-500">
            Add a company, one or more websites, or both.
          </p>
          <button
            type="button"
            class="btn accent mt-5 inline-flex h-9 items-center justify-center whitespace-nowrap px-4"
            @click="startCreate"
          >
            <PlusIcon class="mr-1 h-4 w-4" /> Add competitor
          </button>
        </div>
      </section>

      <aside
        v-if="editing"
        class="sticky top-5 rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
      >
        <div
          class="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-700"
        >
          <h2 class="font-semibold text-slate-900 dark:text-white">
            {{ form.id ? "Edit competitor" : "Add competitor" }}
          </h2>
          <button
            type="button"
            class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
            @click="closeEditor"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
        <form class="space-y-4 p-5" @submit.prevent="save">
          <p
            v-if="fieldError('general')"
            class="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-200"
          >
            {{ fieldError("general") }}
          </p>
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
              >Competitor name</span
            >
            <input
              v-model="form.name"
              required
              class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              maxlength="255"
            />
            <span
              v-if="fieldError('name')"
              class="mt-1 block text-xs text-rose-600"
              >{{ fieldError("name") }}</span
            >
          </label>
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
              >Companies House number</span
            >
            <input
              v-model="form.company_number"
              class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              placeholder="Optional"
              maxlength="20"
            />
            <span
              v-if="fieldError('company_number')"
              class="mt-1 block text-xs text-rose-600"
              >{{ fieldError("company_number") }}</span
            >
            <span class="mt-1 block text-xs text-slate-400"
              >The registered entity is verified before it is linked.</span
            >
          </label>
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
              >Website URLs</span
            >
            <textarea
              v-model="form.website_urls"
              rows="5"
              class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              placeholder="https://example.com&#10;https://example.com/news"
            />
            <span
              v-if="websiteError"
              class="mt-1 block text-xs text-rose-600"
              >{{ websiteError }}</span
            >
            <span class="mt-1 block text-xs text-slate-400"
              >Enter one page per line. Images are downloaded and served by
              Neptune.</span
            >
          </label>
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
              >Check every</span
            >
            <select
              v-model.number="form.monitoring_interval_hours"
              class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            >
              <option :value="6">6 hours</option>
              <option :value="12">12 hours</option>
              <option :value="24">Daily</option>
              <option :value="168">Weekly</option>
            </select>
          </label>
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200"
              >Notes</span
            >
            <textarea
              v-model="form.notes"
              rows="4"
              class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              placeholder="Services, regions, positioning, or other context"
            />
          </label>
          <label
            class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
          >
            <input
              v-model="form.active"
              type="checkbox"
              class="h-4 w-4 rounded border-slate-300 text-cyan-600"
            />
            Monitoring active
          </label>
          <div
            class="flex justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-700"
          >
            <button type="button" class="btn h-9 px-4" @click="closeEditor">
              Cancel
            </button>
            <button
              type="submit"
              class="btn accent inline-flex h-9 items-center justify-center whitespace-nowrap px-4"
              :disabled="saving"
            >
              <ArrowPathIcon
                v-if="saving"
                class="mr-1.5 h-4 w-4 animate-spin"
              />
              {{ form.id ? "Save changes" : "Add competitor" }}
            </button>
          </div>
        </form>
      </aside>
    </div>
  </div>
</template>
